/**
 * Expands range expressions into flat number strings.
 *
 * Supports:
 * - Numeric ranges with optional padding: 003-030 → 003, 004, …, 029, 030
 * - Numeric ranges: 101-132 → 101, 102, …, 132
 * - Alphanumeric ranges (same prefix): G1-G15 → G1, G2, …, G15
 * - Alphanumeric ranges: A1-A101 → A1, A2, …, A99, A100, A101
 * - Right side numeric uses left prefix: A1-101 → A1, A2, …, A101
 * - 2D "until" range: A1-101 until A2-132 → A1-101, A1-102, …, A1-132, A2-101, …, A2-132
 *
 * Multiple ranges in one expression are comma-separated.
 * Example: "003-030, 101-132, G1-G15" or "A1-101 until A2-132, G1-G15"
 */

/**
 * Expand "A1-101 until A2-132" style 2D range (prefix + firstNum-secondNum).
 * @param {string} from - e.g. "A1-101"
 * @param {string} to - e.g. "A2-132"
 * @returns {string[]} Expanded values, or [] if invalid
 */
function expandUntilRange(from, to) {
  const m1 = String(from).trim().match(/^([A-Za-z]*)(\d+)-(\d+)$/);
  const m2 = String(to).trim().match(/^([A-Za-z]*)(\d+)-(\d+)$/);
  if (!m1 || !m2) return [];
  const [, pre1, a1, a2] = m1;
  const [, pre2, b1, b2] = m2;
  if (pre1 !== pre2) return [];
  const start1 = parseInt(a1, 10);
  const start2 = parseInt(a2, 10);
  const end1 = parseInt(b1, 10);
  const end2 = parseInt(b2, 10);
  if (start1 > end1 || start2 > end2) return [];
  const out = [];
  for (let n1 = start1; n1 <= end1; n1++) {
    for (let n2 = start2; n2 <= end2; n2++) {
      out.push(pre1 + n1 + '-' + n2);
    }
  }
  return out;
}

/**
 * Expand a single range spec (e.g. "003-030", "G1-G15", "A1-101 until A2-132") into an array of strings.
 * @param {string} spec - One range, e.g. "003-030" or "G1-G15"
 * @returns {string[]} Expanded values, or [] if invalid
 */
export function expandOneRange(spec) {
  const s = String(spec).trim();
  if (!s) return [];

  const untilIdx = s.toLowerCase().indexOf(' until ');
  if (untilIdx !== -1) {
    const from = s.slice(0, untilIdx).trim();
    const to = s.slice(untilIdx + 7).trim();
    return expandUntilRange(from, to);
  }

  if (!s.includes('-')) return [];

  const dashIdx = s.indexOf('-');
  const left = s.slice(0, dashIdx).trim();
  const right = s.slice(dashIdx + 1).trim();
  if (!left || !right) return [];

  // Numeric: both sides are digits only
  const numLeft = /^\d+$/.test(left);
  const numRight = /^\d+$/.test(right);
  if (numLeft && numRight) {
    const a = parseInt(left, 10);
    const b = parseInt(right, 10);
    if (a > b) return [];
    const padding = left.length;
    const out = [];
    for (let n = a; n <= b; n++) {
      out.push(String(n).padStart(padding, '0'));
    }
    return out;
  }

  // Alphanumeric: prefix + number on left; right can be "number" (use left prefix) or "prefix+number"
  const leftMatch = left.match(/^([A-Za-z]*)(\d+)$/);
  const rightMatch = right.match(/^([A-Za-z]*)(\d+)$/);
  if (!leftMatch || !rightMatch) return [];

  const [, preL, numL] = leftMatch;
  const [, preR, numR] = rightMatch;
  const prefix = preR !== '' ? preR : preL;
  if (preL !== '' && preR !== '' && preL !== preR) return [];

  const a = parseInt(numL, 10);
  const b = parseInt(numR, 10);
  if (a > b) return [];

  const out = [];
  for (let n = a; n <= b; n++) {
    out.push(prefix + String(n));
  }
  return out;
}

/**
 * Expand a full range expression (comma-separated specs) into a sorted, deduplicated array.
 * @param {string} expression - e.g. "003-030, 101-132, G1-G15"
 * @returns {string[]} Expanded flat numbers, deduplicated, order preserved per range
 */
export function expandRangeExpression(expression) {
  if (!expression || typeof expression !== 'string') return [];
  const specs = expression.split(',').map(s => s.trim()).filter(Boolean);
  const seen = new Set();
  const result = [];
  for (const spec of specs) {
    const values = expandOneRange(spec);
    for (const v of values) {
      if (!seen.has(v)) {
        seen.add(v);
        result.push(v);
      }
    }
  }
  return result;
}
